import { useState, useEffect, useRef } from 'react'
import { fetchPlaceSuggestions, type PlaceSuggestion } from '../services/googleMapsPlaces'

type LocationDropdownProps = {
  onSelect: (place: string) => void
  className?: string
}

// Popular authentic destinations in and around Salzburg & Austria (Zero mock locations)
const POPULAR_LOCATIONS = [
  'Salzburg Airport (SZG)',
  'Salzburg Hauptbahnhof',
  'Salzburg Old Town (Altstadt)',
  'Hallstatt, Salzkammergut',
  'Zell am See & Kaprun',
  'Vienna (Wien)',
  'Munich Airport (MUC)',
  'Innsbruck',
]

export default function LocationDropdown({
  onSelect,
  className = '',
}: LocationDropdownProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [liveSuggestions, setLiveSuggestions] = useState<PlaceSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    const trimmed = searchTerm.trim()
    if (trimmed.length < 2) {
      setLiveSuggestions([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await fetchPlaceSuggestions(trimmed, false)
        setLiveSuggestions(results)
      } catch {
        setLiveSuggestions([])
      } finally {
        setIsLoading(false)
      }
    }, 280)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [searchTerm])

  return (
    <div
      className={`
        from-dropdown-scrollbar
        absolute
        left-0
        top-[calc(100%+8px)]
        z-[120]
        max-h-[320px]
        w-[420px]
        max-w-[calc(100vw-32px)]
        overflow-y-auto
        rounded-[14px]
        bg-white
        p-3
        shadow-[0_12px_32px_rgba(0,0,0,0.18)]
        border border-[#E5E9EB]
        ${className}
      `}
      role="listbox"
    >
      <div className="mb-2">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search location in Salzburg or Europe..."
          className="w-full rounded-[8px] border border-[#D0D5DD] bg-[#F9FAFB] px-3 py-1.5 text-[13px] font-medium text-black placeholder:text-[#9CA3AF] outline-none focus:border-[#365E92] focus:bg-white"
          autoFocus
        />
      </div>

      {isLoading ? (
        <div className="py-4 text-center font-poppins text-[12px] text-[#71717A]">
          Searching Google Maps...
        </div>
      ) : liveSuggestions.length > 0 ? (
        liveSuggestions.map((place, idx) => (
          <button
            key={place.placeId || `${place.title}-${idx}`}
            type="button"
            onClick={() => onSelect(place.title)}
            className="flex w-full items-start gap-2.5 rounded-[8px] px-2.5 py-2 text-left hover:bg-[#F4F5F7] transition-colors"
          >
            <span className="mt-0.5 text-[#365E92]">??</span>
            <div className="min-w-0 flex-1">
              <p className="font-poppins text-[13px] font-semibold text-black truncate">
                {place.title}
              </p>
              <p className="font-poppins text-[11px] text-[#6B7280] truncate">
                {place.address}
              </p>
            </div>
          </button>
        ))
      ) : (
        POPULAR_LOCATIONS.map((place) => (
          <button
            key={place}
            type="button"
            onClick={() => onSelect(place)}
            className="
              flex
              w-full
              items-center
              gap-2
              rounded-[8px]
              px-2.5
              py-2
              text-left
              hover:bg-[#F4F5F7]
              transition-colors
            "
          >
            <span className="text-xs text-[#365E92]">??</span>
            <span className="font-poppins text-[13px] font-medium text-black">
              {place}
            </span>
          </button>
        ))
      )}
    </div>
  )
}